/**
 * Family Dashboard Service
 * Handles all family-related API interactions for parent dashboard
 */

import { apiClient } from './apiClient';

class FamilyService {
  /**
   * Get family dashboard data
   */
  async getFamilyDashboard(familyId: string, timeRange: string = 'week'): Promise<any> {
    try {
      const params = { time_range: timeRange, include_detailed_sessions: 'true' };
      const response = await apiClient.get<any>(`/families/${familyId}/dashboard`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching family dashboard:', error);
      throw error;
    }
  }

  /**
   * Get family notifications with pagination
   */
  async getNotifications(
    familyId: string,
    page: number = 1,
    pageSize: number = 20,
    unreadOnly: boolean = false
  ): Promise<any> {
    try {
      const params: Record<string, string> = {
        page: page.toString(),
        pageSize: pageSize.toString(),
      };

      if (unreadOnly) {
        params.unread_only = 'true';
      }

      return await apiClient.get<any>(`/families/${familyId}/notifications`, { params });
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  /**
   * Mark notification as read
   */
  async markNotificationAsRead(familyId: string, notificationId: string): Promise<any> {
    try {
      return await apiClient.put<any>(`/families/${familyId}/notifications/${notificationId}/read`, {});
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Get family communications (teacher messages, etc.)
   */
  async getCommunications(
    familyId: string,
    studentId?: string,
    communicationType?: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<any> {
    try {
      const params: Record<string, string> = {
        page: page.toString(),
        pageSize: pageSize.toString(),
      };

      if (studentId) {
        params.student_id = studentId;
      }

      if (communicationType) {
        params.type = communicationType;
      }

      return await apiClient.get<any>(`/families/${familyId}/communications`, { params });
    } catch (error) {
      console.error('Error fetching communications:', error);
      throw error;
    }
  }

  /**
   * Update communication preferences
   */
  async updateCommunicationPreferences(familyId: string, preferences: any): Promise<any> {
    try {
      return await apiClient.put<any>(`/families/${familyId}/communication-preferences`, preferences);
    } catch (error) {
      console.error('Error updating communication preferences:', error);
      throw error;
    }
  }

  /**
   * Get student work samples with R2 access
   */
  async getStudentWorkSamples(
    familyId: string,
    studentId: string,
    subject?: string,
    dateRange: string = 'month'
  ): Promise<any> {
    try {
      const params: Record<string, string> = {
        date_range: dateRange,
      };

      if (subject) {
        params.subject = subject;
      }

      return await apiClient.get<any>(`/families/${familyId}/students/${studentId}/work-samples`, { params });
    } catch (error) {
      console.error('Error fetching work samples:', error);
      throw error;
    }
  }
}

export const familyService = new FamilyService();
export default familyService;
